// The single-player Distribution view (04h-5b) — a second lens on the same radar axes, ported
// from renderers-radar.js's renderRadarDistribution / distRowHtml / distRowSvg. Where the wheel
// plots this player's value as one point, this plots the WHOLE population's density for each axis
// (a kernel-density curve, the 04h-5a pure layer) and marks where the player falls within it.
//
// Plain React JSX (static SVG paths + a CSS-triangle marker per row; no d3, no interaction) — the
// path/marker math is all pure (radar-view.ts), so this file is just the markup. It renders into
// the same .radar-table-wrap the Metric/Min/Value/Max table uses — the two views are mutually
// exclusive per module (renderers-radar.js:991), so they share the one slot.

import { useMemo } from "react";
import {
  densityCurve,
  distMarkerFrac,
  distAreaPaths,
  distSingleAreaPath,
  percentileRank,
  truncateLabel,
  AXIS_LABEL_MAX_LEN,
  DIST_ROW_W,
  DIST_ROW_H,
  DIST_BASELINE_Y,
  type DistSpread,
  type RadarAxis,
} from "./radar-view.js";
import { formatMetricValue } from "../engines/stats-filter-kit.js";

interface Props {
  /** Who each row is drawn against (radar-view.ts playerSpread; a team's, teamSpread). */
  spread: DistSpread;
  /** The chart axes (no Minutes row — Distribution mirrors the wheel's axis set). */
  axes: readonly RadarAxis[];
  /** The player's merged value per axis (radarMetrics-over-stats). */
  values: Record<string, number | null>;
}

interface DistRow {
  key: string;
  shortLabel: string;
  labelTooltip: string | undefined;
  hasPoints: boolean;
  hasValue: boolean;
  leftPath: string;
  rightPath: string;
  singleArea: string;
  markerXPct: number | null;
  markerYPct: number | null;
  markerCls: string;
  valueText: string;
  pctile: number | null;
  pctileCls: string;
  leftTick: string;
  midTick: string;
  rightTick: string;
}

export function RadarDistribution({ spread, axes, values }: Props) {

  const rows = useMemo<DistRow[]>(() => {
    return axes.map((axis) => {
      const popValues = spread.valuesOf(axis.key);
      const domainMin = popValues.length ? popValues[0]! : 0;
      const domainMax = popValues.length ? popValues[popValues.length - 1]! : 0;
      const lowerBetter = spread.lowerBetter(axis.key);
      const points = densityCurve(popValues, lowerBetter);
      const rawValue = values[axis.key];
      const hasValue = rawValue != null && !Number.isNaN(rawValue);
      const pctile = hasValue ? percentileRank(popValues, rawValue as number) : null;
      // Colour cue agrees with the axis's real polarity — a high percentile is "good" unless the
      // metric is lower-is-better (renderers-radar.js:844).
      const goodDirection = pctile === null ? true : lowerBetter ? pctile <= 50 : pctile >= 50;
      const markerFrac = hasValue ? distMarkerFrac(rawValue as number, domainMin, domainMax, lowerBetter) : 0.5;
      const clamped = Math.max(0, Math.min(1, markerFrac));

      let leftPath = "";
      let rightPath = "";
      let singleArea = "";
      let markerXPct: number | null = null;
      let markerYPct: number | null = null;
      if (points.length) {
        if (hasValue) {
          const a = distAreaPaths(points, clamped);
          leftPath = a.leftPath;
          rightPath = a.rightPath;
          markerXPct = (a.markerX / DIST_ROW_W) * 100;
          markerYPct = (a.markerY / DIST_ROW_H) * 100;
        } else {
          // An axis the player has no value for — the plain population curve on its own, no marker,
          // no coloured split (honest, not a fabricated "average"). renderers-radar.js:769.
          singleArea = distSingleAreaPath(points);
        }
      }

      const midValue = domainMin + (domainMax - domainMin) / 2;
      // The good end is always on the right, so a lower-is-better axis runs max→min left→right.
      const leftDomainValue = lowerBetter ? domainMax : domainMin;
      const rightDomainValue = lowerBetter ? domainMin : domainMax;

      return {
        key: axis.key,
        shortLabel: truncateLabel(axis.label, AXIS_LABEL_MAX_LEN),
        labelTooltip: axis.label.length > AXIS_LABEL_MAX_LEN ? axis.label : undefined,
        hasPoints: points.length > 0,
        hasValue,
        leftPath,
        rightPath,
        singleArea,
        markerXPct,
        markerYPct,
        markerCls: goodDirection ? "radar-dist-marker-good" : "radar-dist-marker-bad",
        valueText: formatMetricValue(rawValue, axis.outputType),
        pctile,
        pctileCls: pctile === null ? "" : goodDirection ? "radar-dist-pctile-good" : "radar-dist-pctile-bad",
        leftTick: formatMetricValue(leftDomainValue, axis.outputType),
        midTick: formatMetricValue(midValue, axis.outputType),
        rightTick: formatMetricValue(rightDomainValue, axis.outputType),
      };
    });
  }, [spread, axes, values]);

  return (
    <>
      <div className="radar-dist-header">
        <span className="radar-dist-header-title">Distribution</span>
        <span className="radar-dist-header-sub">{spread.sub}</span>
      </div>
      <div className="radar-dist-row radar-dist-col-headers">
        <span className="radar-dist-label" />
        <span className="radar-dist-chart-wrap" />
        <span className="radar-dist-value">Value</span>
        <span className="radar-dist-pctile">%ile</span>
      </div>
      <div className="radar-dist-rows">
        {rows.map((r) => (
          <div className="radar-dist-row" data-key={r.key} key={r.key}>
            <span className="radar-dist-label" {...(r.labelTooltip ? { "data-tooltip": r.labelTooltip } : {})}>
              {r.shortLabel}
            </span>
            <div className="radar-dist-chart-wrap">
              <div className="radar-dist-svg-wrap">
                <svg className="radar-dist-svg" viewBox={`0 0 ${DIST_ROW_W} ${DIST_ROW_H}`} preserveAspectRatio="none">
                  {r.hasPoints && (
                    <>
                      <line className="radar-dist-baseline" x1="0" y1={DIST_BASELINE_Y} x2={DIST_ROW_W} y2={DIST_BASELINE_Y} />
                      {r.hasValue ? (
                        <>
                          <path className="radar-dist-area radar-dist-area-below" d={r.leftPath} />
                          <path className="radar-dist-area radar-dist-area-above" d={r.rightPath} />
                        </>
                      ) : (
                        <path className="radar-dist-area radar-dist-area-above" d={r.singleArea} />
                      )}
                    </>
                  )}
                </svg>
                {r.markerXPct != null && r.markerYPct != null && (
                  <div
                    className={`radar-dist-marker-tri ${r.markerCls}`}
                    style={{ left: `${r.markerXPct.toFixed(2)}%`, top: `${r.markerYPct.toFixed(2)}%` }}
                  />
                )}
              </div>
              <div className="radar-dist-ticks">
                <span className="radar-dist-tick" style={{ left: "0%" }}>
                  {r.leftTick}
                </span>
                <span className="radar-dist-tick" style={{ left: "50%" }}>
                  {r.midTick}
                </span>
                <span className="radar-dist-tick radar-dist-tick-end" style={{ left: "100%" }}>
                  {r.rightTick}
                </span>
              </div>
            </div>
            <span className="radar-dist-value">{r.valueText}</span>
            <span className={`radar-dist-pctile ${r.pctileCls}`}>{r.pctile === null ? "—" : r.pctile}</span>
          </div>
        ))}
      </div>
    </>
  );
}
